import React from 'react';
import {useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {STATUSES} from '@chekin/core';
import {cn, isDarkColor} from '@chekinapp/ui';
import {useIsOffline, useRetryAppLoad} from 'hooks';
import {TrustFooter} from 'components/TrustFooter';
import {
  EntranceBackdrop,
  EntranceScreen,
  useEntranceKind,
} from 'components/EntranceScreen';
import {isTerminalSession} from 'hooks/terminalMode';
import {useSummary} from 'context/summary';
import {useChekinSDK} from 'context/ChekinSDK';
import {useBrandingTheme} from 'context/brandingTheme';
import {
  SPLASH_FADE_TRANSITION_CLASS,
  SPLASH_QUERY_PARAM,
  SPLASH_TIMING,
  getAnnounceKey,
} from './constants';
import {useSplashController} from './useSplashController';
import {useAppSurfaceSettled} from './useAppSurfaceSettled';
import {useSplashCoverStore} from './useSplashCoverStore';
import {SplashCurtains} from './SplashCurtains';
import {SplashLogoPlate} from './SplashLogoPlate';
import {SplashStatus} from './SplashStatus';
import {DARK_INK_VARS, LIGHT_INK_VARS} from './splashInk';

function SplashScreen() {
  const {t} = useTranslation();
  const retryAppLoad = useRetryAppLoad();
  const {isAuthInitializationError, isSuccessSDK, hasToken} = useChekinSDK();
  const {summaryStatus, reservationId} = useSummary();
  const {data, isLoadedTemplate} = useBrandingTheme();
  const [searchParams] = useSearchParams();
  const rootRef = React.useRef<HTMLDivElement>(null);

  const [splashParam] = React.useState(() => searchParams.get(SPLASH_QUERY_PARAM));
  const isForced = splashParam === 'true';
  const isOptedOut = splashParam === 'false';
  const isDisabledByBranding =
    !isForced && isLoadedTemplate && data?.splash_screen_enabled === false;
  const isFailed = isAuthInitializationError || summaryStatus === STATUSES.error;
  const isOffline = useIsOffline();
  const isSummaryPending =
    Boolean(reservationId) &&
    summaryStatus !== STATUSES.success &&
    summaryStatus !== STATUSES.error;
  const isDataReady = isSuccessSDK && !isSummaryPending;
  const isAppSurfaceSettled = useAppSurfaceSettled(isDataReady, rootRef);

  const {
    kind: detectedEntrance,
    isSkipped,
    isSettled: isEntranceSettled,
  } = useEntranceKind();
  const entranceKind = isOptedOut || isDisabledByBranding ? null : detectedEntrance;
  const isEntrancePending =
    !isEntranceSettled && !isOptedOut && !isDisabledByBranding && !isTerminalSession();
  const [isEntranceCovering, setIsEntranceCovering] = React.useState(false);
  const [wasBackdropShown, setWasBackdropShown] = React.useState(false);
  const [isBackdropGone, setIsBackdropGone] = React.useState(false);
  if (isEntrancePending && !wasBackdropShown) {
    setWasBackdropShown(true);
  }
  const isBackdropLeaving = isSkipped && wasBackdropShown && !isBackdropGone;

  React.useEffect(
    function dropBackdropAfterFade() {
      if (!isBackdropLeaving) {
        return;
      }

      const timeout = setTimeout(() => setIsBackdropGone(true), SPLASH_TIMING.curtain);
      return () => clearTimeout(timeout);
    },
    [isBackdropLeaving],
  );
  const handleEntranceCovered = React.useCallback(() => setIsEntranceCovering(true), []);

  const {phase, exit, stage, elapsedMs, restart} = useSplashController({
    isReady: isDataReady && isAppSurfaceSettled && isEntranceSettled && !entranceKind,
    shouldDismiss: isFailed || isDisabledByBranding || isOffline,
  });

  const cover =
    isOptedOut || isSkipped || !hasToken || phase === 'gone'
      ? 'none'
      : phase === 'visible'
      ? 'covering'
      : exit === 'handoff'
      ? 'revealing'
      : 'none';
  const setCover = useSplashCoverStore(state => state.setCover);
  React.useEffect(
    function reportCoverToStore() {
      if (entranceKind) {
        return;
      }

      setCover(cover);
      return () => setCover('none');
    },
    [cover, setCover, entranceKind],
  );

  const handleRetry = () => {
    retryAppLoad();
    restart();
  };

  const hostLogo = data?.splash_screen_logo;
  const isDark = isDarkColor(data?.splash_screen_background_color);
  const isHandingOff = phase === 'closing' || phase === 'fading';
  const areCurtainsClosed = exit === 'handoff' && phase !== 'visible';

  const announceKey = getAnnounceKey(stage, isHandingOff);

  const splash = (
    <div
      ref={rootRef}
      role="status"
      aria-live="polite"
      aria-busy={phase === 'visible'}
      className={cn(
        'fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden px-6 pb-[92px] pt-6 kiosko:pb-[130px]',
        'transition-[opacity,visibility] [transition-timing-function:ease] motion-reduce:transition-none',
        phase === 'fading' && 'invisible opacity-0',
      )}
      style={{
        background: 'var(--splash-bg)',
        transitionDuration: `${SPLASH_TIMING.curtain}ms`,
        ...(isDark ? DARK_INK_VARS : LIGHT_INK_VARS),
      }}
    >
      <SplashCurtains areClosed={areCurtainsClosed} />

      <SplashLogoPlate hostLogo={hostLogo} upsized />

      <SplashStatus
        stage={stage}
        elapsedMs={elapsedMs}
        onRetry={handleRetry}
        isHandingOff={isHandingOff}
      />

      <span className="sr-only">{announceKey ? t(announceKey) : null}</span>

      <TrustFooter
        isDark={isDark}
        kiosk
        className={cn(
          SPLASH_FADE_TRANSITION_CLASS,
          isHandingOff && 'translate-y-[6px] opacity-0',
        )}
      />
    </div>
  );

  if (entranceKind) {
    return (
      <>
        {!isEntranceCovering && (
          <EntranceBackdrop stage={stage} elapsedMs={elapsedMs} onRetry={handleRetry} />
        )}
        <EntranceScreen
          kind={entranceKind}
          isDataReady={isDataReady}
          shouldDismiss={isFailed || isOffline}
          onCovered={handleEntranceCovered}
          onRetry={retryAppLoad}
        />
      </>
    );
  }

  if (isBackdropLeaving) {
    return (
      <EntranceBackdrop
        stage={stage}
        elapsedMs={elapsedMs}
        onRetry={handleRetry}
        isLeaving
      />
    );
  }

  if (isOptedOut || isSkipped || !hasToken || phase === 'gone') {
    return null;
  }

  if (isEntrancePending) {
    return <EntranceBackdrop stage={stage} elapsedMs={elapsedMs} onRetry={handleRetry} />;
  }

  return splash;
}

export {SplashScreen};
